/**
 * Area Container Management
 * Organizes all nodes under Areas (PARA-style organization)
 * Default area: "Inbox" for quick capture
 */

import { supabase } from './supabase'

export type AreaType = 'inbox' | 'custom'

/**
 * Get or create Inbox area (default for all new/synced nodes)
 * Returns the Inbox container node ID
 */
export async function getInboxAreaId(userId: string): Promise<string> {
  return getAreaContainerId(userId, 'Inbox')
}

/**
 * Get or create area container for a given area name
 * Returns the container node ID
 */
export async function getAreaContainerId(
  userId: string,
  areaName: string
): Promise<string> {
  // Try to find existing area container
  // NOTE: Use .limit(1) instead of .single() to handle duplicates gracefully
  const { data: existing, error: findError } = await supabase
    .from('nodes')
    .select('id')
    .eq('user_id', userId)
    .eq('content', areaName)
    .eq('type', 'heading')
    .contains('tags', ['area'])
    .order('created_at', { ascending: true }) // Use oldest container
    .limit(1)

  if (existing && existing.length > 0 && !findError) {
    return existing[0].id
  }

  // Create area if it doesn't exist
  console.log(`[AreaContainer] Creating ${areaName} area for user ${userId}`)

  const { data: newArea, error: createError } = await supabase
    .from('nodes')
    .insert({
      user_id: userId,
      content: areaName,
      type: 'heading',
      tags: ['area'],
      position: areaName === 'Inbox' ? 0 : 999, // Inbox always first
    })
    .select('id')
    .single()

  if (createError) {
    console.error(`[AreaContainer] Failed to create ${areaName} area:`, createError)
    throw new Error(`Failed to create area container: ${createError.message}`)
  }

  return newArea.id
}

/**
 * Create a new custom area
 */
export async function createArea(
  userId: string,
  areaName: string,
  options?: {
    description?: string
    position?: number
  }
): Promise<string> {
  console.log(`[AreaContainer] Creating custom area: ${areaName}`)

  const { data: newArea, error } = await supabase
    .from('nodes')
    .insert({
      user_id: userId,
      content: areaName,
      type: 'heading',
      tags: ['area'],
      note: options?.description || '',
      position: options?.position ?? 999,
    })
    .select('id')
    .single()

  if (error) {
    console.error(`[AreaContainer] Failed to create area:`, error)
    throw new Error(`Failed to create area: ${error.message}`)
  }

  return newArea.id
}

/**
 * Get all areas for a user
 */
export async function getAllAreas(userId: string) {
  const { data, error } = await supabase
    .from('nodes')
    .select('*')
    .eq('user_id', userId)
    .contains('tags', ['area'])
    .order('position')

  if (error) {
    console.error('[AreaContainer] Failed to fetch areas:', error)
    return []
  }

  return data || []
}

/**
 * Move a node to a different area
 */
export async function moveNodeToArea(
  nodeId: string,
  newAreaId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('nodes')
    .update({ parent_id: newAreaId })
    .eq('id', nodeId)

  if (error) {
    console.error('[AreaContainer] Failed to move node:', error)
    return false
  }

  return true
}

/**
 * Delete an area (moves children to Inbox first)
 */
export async function deleteArea(
  userId: string,
  areaId: string
): Promise<boolean> {
  // Get Inbox ID
  const inboxId = await getInboxAreaId(userId)

  // Move all children to Inbox
  const { error: moveError } = await supabase
    .from('nodes')
    .update({ parent_id: inboxId })
    .eq('parent_id', areaId)

  if (moveError) {
    console.error('[AreaContainer] Failed to move children to Inbox:', moveError)
    return false
  }

  // Delete the area
  const { error: deleteError } = await supabase
    .from('nodes')
    .delete()
    .eq('id', areaId)

  if (deleteError) {
    console.error('[AreaContainer] Failed to delete area:', deleteError)
    return false
  }

  return true
}
